'use client';

import React from 'react';
import { m } from 'framer-motion';
import { Navbar } from '@/components/landing/Navbar';
import { Footer } from '@/components/landing/Footer';
import { PricingTable } from '@/components/landing/PricingTable';
import { Building2, ArrowRight, Check } from 'lucide-react';
import Link from 'next/link';

export default function PricingPage() {
    return (
        <div className="min-h-screen bg-[#FAFAFA] text-neutral-900 font-sans selection:bg-orange-100 selection:text-orange-900">
            <Navbar />

            <main className="pt-32 pb-24">
    
                {/* Pricing Table Component */}
                <PricingTable />

                {/* Enterprise Section */}
                <section className="py-24 px-6">
                    <div className="max-w-6xl mx-auto">
                        <m.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className="relative p-10 md:p-16 rounded-[2.5rem] bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900 border border-neutral-700 overflow-hidden"
                        >
                            {/* Background Pattern */}
                            <div className="absolute inset-0 opacity-10">
                                <div className="absolute top-0 left-0 w-96 h-96 bg-orange-500 rounded-full blur-[128px]" />
                                <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500 rounded-full blur-[128px]" />
                            </div>

                            <div className="relative z-10 grid md:grid-cols-2 gap-12 items-center">
                                <div>
                                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-sm font-semibold mb-6">
                                        <Building2 size={16} />
                                        Enterprise
                                    </div>
                                    <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                                        Custom AI Infrastructure for Scale
                                    </h2>
                                    <p className="text-lg text-neutral-400 mb-8 leading-relaxed">
                                        Beyond standard tooling. We architect bespoke AI ecosystems that integrate
                                        deeply with your existing stack. Dedicated GPU clusters, private deployments,
                                        and a solutions architect on call.
                                    </p>
                                    <Link href="/enterprise">
                                        <button className="px-8 py-4 bg-orange-600 hover:bg-orange-500 text-white font-bold rounded-xl transition-all shadow-lg hover:shadow-orange-500/25 flex items-center gap-2">
                                            Talk to Enterprise Sales
                                            <ArrowRight size={20} />
                                        </button>
                                    </Link>
                                </div>

                                <div className="space-y-4">
                                    {[
                                        'Custom Model Fine-tuning',
                                        'On-Premise or Private Cloud Deployment',
                                        'Enterprise-grade Security & Compliance (SOC2)',
                                        'Dedicated Solutions Architect',
                                        '24/7 Priority Support Channel',
                                        'Multi-brand Management',
                                        'API-first Access & Custom Integrations',
                                        'White-glove Onboarding & Training'
                                    ].map((feature, i) => (
                                        <div key={i} className="flex items-center gap-3 text-white/90">
                                            <div className="p-1 rounded-full bg-orange-500/20 text-orange-400">
                                                <Check size={14} strokeWidth={3} />
                                            </div>
                                            <span className="font-medium">{feature}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </m.div>
                    </div>
                </section>

                {/* FAQ Teaser */}
                <section className="py-16 px-6 text-center">
                    <p className="text-neutral-500 font-medium">
                        Have questions? Check out our{' '}
                        <Link href="/faq" className="text-orange-600 hover:underline font-semibold">
                            FAQ
                        </Link>{' '}
                        or{' '}
                        <a href="mailto:support@axist.app" className="text-orange-600 hover:underline font-semibold">
                            contact support
                        </a>.
                    </p>
                </section>
            </main>

            <Footer />
        </div>
    );
}
